// lib
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Dialog, DialogTitle, DialogContent, List } from "@material-ui/core";
import { useTranslation } from "react-i18next";

// src
import { currentLocalizer } from "i18n/localizer";
import { capitalizeFirstLetter } from "utils/string";
import Item from "./EventItem";

const arrayColumn = (arr, n) => arr.map((x) => x[n]);

const formatAuthors = (event) => {
  if (!event) return [];
  const authors = event.teachers && event.teachers.length ? event.teachers : [event.author];
  return authors.map((author) => {
    const name = `${author.firstname} ${author.lastname}`;
    return author.email ? { name, email: author.email } : { name };
  });
};

const StyledModal = ({ open, identity, onClose, event }) => {
  const { language } = useSelector((state) => state.ux);
  const { t } = useTranslation();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.keyCode === 27) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (!event) return null;
  const authors = formatAuthors(event);
  const localizer = currentLocalizer(language);
  return (
    <Dialog
      hideBackdrop
      className="app-modal"
      onClose={onClose}
      aria-labelledby="details"
      open={open}
      scroll="paper"
    >
      <DialogTitle className="title">{event.name}</DialogTitle>
      <DialogContent className="content">
        <List dense>
          <Item
            type="schedule"
            contents={[
              `${localizer.format(event.start, "HH:mm")}-${localizer.format(
                event.end,
                "HH:mm",
              )} — ${localizer.format(event.start, "DD/MM/YYYY")} — ${capitalizeFirstLetter(
                event.type.name,
              )}`,
            ]}
            key="schedule"
          />
          {event.rooms && !!event.rooms.length && (
            <Item
              type="rooms"
              contents={event.rooms.map(({ name }) => name)}
              links={event.rooms.map(({ mapwizeLink }) => mapwizeLink)}
              key="rooms"
            />
          )}
          {authors && !!authors.length && (
            <Item
              type="authors"
              contents={authors.map(({ name }) => name)}
              emails={authors.map(({ email }) => email)}
              key="authors"
            />
          )}
          {event.groups && !!event.groups.length && (
            <Item
              type="groups"
              contents={event.groups.map(({ name }) => name).join(", ")}
              key="groups"
            />
          )}
          {event.countStudents > 0 && (
            <Item
              type="countStudent"
              contents={
                event.countStudents +
                " " +
                (event.countStudents > 1 ? t("details.students") : t("details.student"))
              }
              key="countStudent"
            />
          )}
          {event.countOptionalAttendees > 0 && (
            <Item
              type="countOptionalAttendees"
              contents={
                event.countOptionalAttendees +
                " " +
                (event.countOptionalAttendees > 1
                  ? t("details.optionalAttendees")
                  : t("details.optionalAttendee"))
              }
              key="countOptionalAttendees"
            />
          )}
          {event.countMandatoryAttendees > 0 && (
            <Item
              type="countMandatoryAttendees"
              contents={
                event.countMandatoryAttendees +
                " " +
                (event.countMandatoryAttendees > 1
                  ? t("details.mandatoryAttendees")
                  : t("details.mandatoryAttendee"))
              }
              key="countMandatoryAttendees"
            />
          )}
          {event.teachers && arrayColumn(event.teachers, "id").includes(identity.id) && (
            <Item
              type="trombinoscope"
              contents={event.trombinoscope.map(() => "Trombinoscope")}
              links={event.trombinoscope.map(({ links }) => window.location.origin + links)}
              key="trombinoscope"
            />
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
};

StyledModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  event: PropTypes.object,
  identity: PropTypes.object,
};

StyledModal.defaultProps = {
  event: null,
  identity: { id: "", firstName: "", lastName: "" },
};

export default StyledModal;
